import type { ReactNode } from 'react';
import ModuleLayout, { type BreadcrumbItem } from '@/components/admin/ModuleLayout';
import { EntityDetailNavLayout, type EntityDetailNavGroup } from '@/components/admin/EntityDetailNav';

export type TeamTabKey = 'info' | 'members' | 'payments' | 'negotiation' | 'ficha' | 'audit';

const TEAM_TAB_LABELS: Record<TeamTabKey, string> = {
  info: 'Información',
  members: 'Miembros',
  payments: 'Pagos',
  negotiation: 'Negociación',
  ficha: 'Ficha operativa',
  audit: 'Auditoría',
};

interface TeamShellProps {
  mode: 'create' | 'edit';
  activeTab: TeamTabKey;
  onTabChange: (tab: TeamTabKey) => void;
  breadcrumbs: BreadcrumbItem[];
  title: string;
  subtitle: string;
  headerActions?: ReactNode;
  tabLabels?: Partial<Record<TeamTabKey, string>>;
  children: ReactNode;
}

// Shared shell for Team create/view/edit (cccd-ux-redesign — cccd-admin-tabbed-shell):
// creation renders the same tab bar as viewing/editing, with only Información
// enabled and the rest visible-but-locked until the team is saved.
export function TeamShell({ mode, activeTab, onTabChange, breadcrumbs, title, subtitle, headerActions, tabLabels, children }: TeamShellProps) {
  const locked = (key: TeamTabKey) => mode === 'create' && key !== 'info';
  const label = (key: TeamTabKey) => tabLabels?.[key] ?? TEAM_TAB_LABELS[key];
  const groups: EntityDetailNavGroup<TeamTabKey>[] = [
    { label: 'GENERAL', items: [
      { key: 'info', label: label('info'), locked: locked('info') },
      { key: 'ficha', label: label('ficha'), locked: locked('ficha') },
    ] },
    { label: 'GESTIÓN', items: [
      { key: 'members', label: label('members'), locked: locked('members') },
      { key: 'payments', label: label('payments'), locked: locked('payments') },
      { key: 'negotiation', label: label('negotiation'), locked: locked('negotiation') },
    ] },
    { label: 'SISTEMA', items: [
      { key: 'audit', label: label('audit'), locked: locked('audit') },
    ] },
  ];

  return (
    <ModuleLayout
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={subtitle}
      tabs={[
        { label: 'Panel', to: '/cccd' },
        { label: 'Nacionales', to: '/cccd/events' },
        { label: 'Regionales', to: '/cccd/regionals' },
      ]}
      metrics={[]}
      actions={headerActions}
    >
      <EntityDetailNavLayout groups={groups} activeKey={activeTab} onChange={(key) => !locked(key) && onTabChange(key)}>
        {children}
      </EntityDetailNavLayout>
    </ModuleLayout>
  );
}
